import { computed, onBeforeUnmount, ref, watch, watchEffect, type Ref } from "vue";
import { publishMapSheetInset, releaseMapSheetInset } from "./useMapSheetInset";

// Общая механика мобильных шторок карточек (маршрут - MobileRouteDetailSheet,
// точка - MobilePointSheet): свободная высота, перетаскивание за ручку/шапку,
// «доводчик» к высоте одной шапки, публикация занятой высоты для тулбара карты.
//
// Свободная высота (без фиксированных снапов) - пользователь тянет sheet на
// любую высоту, отпускает - там и остаётся. Низ - высота одной только шапки
// (headerHeight), верх - 90% экрана. DRAG_HANDLE_HEIGHT - высота статичной
// ручки над шапкой.
export const DRAG_HANDLE_HEIGHT = 14;
// 6px - отступ шторки от низа (bottom-1.5).
const SHEET_BOTTOM_GAP = 6;

export function useDragSheet(opts: {
  // Высота открытия: шапка + сводка карточки (замер ResizeObserver'ом).
  compactHeight: Ref<number>;
  headerHeight: Ref<number>;
  // Смена объекта - снова высота открытия (как будто пользователь не тянул).
  resetKey: () => unknown;
  // Шторка видна (не накрыта другой): только тогда публикует свой отступ.
  active?: () => boolean;
  // Зазор шторки от низа, px (по умолчанию 6: «плавающая» карточка; 0 - шторка
  // вплотную к нижней панели).
  bottomGap?: number;
  // Верхняя граница высоты, px (по умолчанию 90% окна). Шторка маршрута
  // тянется на всю высоту карты, чтобы перекрыть кнопки темы/языка сверху.
  getMaxHeight?: () => number;
}) {
  const active = opts.active ?? (() => true);
  const getVH = () => (typeof window !== "undefined" ? window.innerHeight : 800);
  // До первого измерения headerHeight - 0; нижняя граница в этот момент -
  // просто высота ручки, чтобы sheet не схлопнулся в ничто на первом кадре.
  const minHeight = computed(() => Math.round(opts.headerHeight.value + DRAG_HANDLE_HEIGHT));
  // 0.90, а не ближе к 1 - в некоторых браузерах (например, верхняя панель
  // Vivaldi) window.innerHeight не исключает системный UI.
  const maxHeight = () => Math.round(opts.getMaxHeight?.() || getVH() * 0.9);

  const height = ref(0);
  // Пока пользователь ни разу не потянул sheet сам, высота открытия следует за
  // compactHeight; первый же жест останавливает это отслеживание для текущего
  // объекта, иначе следующий ResizeObserver-тик откатил бы выбранную высоту.
  const userAdjusted = ref(false);
  watchEffect(() => {
    if (userAdjusted.value) return;
    height.value = opts.compactHeight.value > 0
      ? Math.round(opts.compactHeight.value + DRAG_HANDLE_HEIGHT)
      : Math.round(getVH() * 0.3);
  });

  // null, когда перетаскивания нет - тогда высота равна height (анимированно).
  const liveHeight = ref<number | null>(null);
  const sheetHeight = computed(() => liveHeight.value ?? height.value);
  const isDragging = ref(false);

  // Публикуем занятую высоту, чтобы тулбар карты вставал над шторкой.
  // Владелец - сама шторка (см. releaseMapSheetInset): закрытие точки поверх
  // маршрута не обнуляет отступ, который маршрут уже снова опубликовал.
  const insetOwner = Symbol("drag-sheet");
  watchEffect(() => {
    // Скрыта (накрыта другой шторкой) - отступ снят (если он ещё её).
    if (!active()) {
      releaseMapSheetInset(insetOwner);
      return;
    }
    publishMapSheetInset(insetOwner, Math.round(sheetHeight.value) + (opts.bottomGap ?? SHEET_BOTTOM_GAP));
  });
  onBeforeUnmount(() => releaseMapSheetInset(insetOwner));

  // Единый жест для всей шторки (ручка + шапка с data-sheet-drag-handle):
  // перетаскивание только за ручку (~16px) ненадёжно. Драг по телу контента
  // всегда нативный скролл.
  let dragStartY = 0;
  let dragStartX = 0;
  let dragStartHeight = 0;
  let dragMode: "resize" | "ignore" | null = null;
  let dragFromHandle = false;
  // Касание по кнопке/ссылке в шапке - не «тап по заголовку».
  let dragFromControl = false;

  const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);

  const removeListeners = () => {
    window.removeEventListener("pointermove", onSheetPointerMove);
    window.removeEventListener("pointerup", onSheetPointerUp);
    window.removeEventListener("pointercancel", onSheetPointerUp);
  };

  const onSheetPointerDown = (e: PointerEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest("[data-sheet-no-drag]")) return;
    dragFromHandle = !!target.closest("[data-sheet-drag-handle]");
    dragFromControl = !!target.closest("button, a, input, select, textarea");
    dragStartY = e.clientY;
    dragStartX = e.clientX;
    dragStartHeight = sheetHeight.value;
    dragMode = null;
    window.addEventListener("pointermove", onSheetPointerMove, { passive: false });
    window.addEventListener("pointerup", onSheetPointerUp);
    window.addEventListener("pointercancel", onSheetPointerUp);
  };

  function onSheetPointerMove(e: PointerEvent) {
    const dy = e.clientY - dragStartY;
    const dx = e.clientX - dragStartX;
    if (dragMode === null) {
      if (Math.abs(dy) < 6 && Math.abs(dx) < 6) return;
      // Жест по телу контента или преимущественно горизонтальный - не ресайз.
      if (!dragFromHandle || Math.abs(dx) > Math.abs(dy)) {
        dragMode = "ignore";
        return;
      }
      dragMode = "resize";
      isDragging.value = true;
    }
    if (dragMode !== "resize") return;
    e.preventDefault();
    liveHeight.value = clamp(dragStartHeight - dy, minHeight.value, maxHeight());
  }

  function onSheetPointerUp() {
    removeListeners();
    // Тап по заголовку свёрнутой шторки - снова высота открытия.
    if (dragMode === null && dragFromHandle && !dragFromControl && isCollapsed()) userAdjusted.value = false;
    if (dragMode === "resize" && liveHeight.value != null) {
      userAdjusted.value = true;
      // «Доводчик» только у нижней границы: отпустили близко к высоте одной
      // шапки - ровно до неё; иначе остаётся как есть.
      const min = minHeight.value;
      const threshold = Math.max(56, (maxHeight() - min) * 0.08);
      height.value = liveHeight.value - min < threshold ? min : liveHeight.value;
    }
    liveHeight.value = null;
    isDragging.value = false;
    dragMode = null;
  }

  onBeforeUnmount(removeListeners);

  // Тап по пустой карте / начало её перетаскивания (App.vue, событие
  // map-interact из MapView): свернуть до одной шапки - не закрывать.
  // Развернуть - тапом по заголовку или жестом вверх.
  const isCollapsed = () => liveHeight.value == null && height.value <= minHeight.value + 1;
  const collapse = () => {
    if (isDragging.value) return;
    userAdjusted.value = true;
    height.value = minHeight.value;
  };

  // Смена вкладки в карточке при шторке, свёрнутой до одной шапки: контент
  // под шапкой не виден - разворачиваем на полную высоту.
  const expandIfCollapsed = () => {
    if (!isCollapsed()) return;
    userAdjusted.value = true;
    height.value = maxHeight();
  };

  watch(opts.resetKey, () => {
    userAdjusted.value = false;
  });

  return { sheetHeight, isDragging, onSheetPointerDown, collapse, expandIfCollapsed };
}
